import { isNil } from 'lodash';
import { CSSProperties, useContext } from 'react';
import { View as RNView, StyleSheet } from 'react-native';
import { MBClearButton } from '@shared/components/MBButton';
import { MBView } from '@shared/components/MBView';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import {
  getComponentIcon,
  getComponentLabel,
  getComponentPlaceholder,
  isDisplayableComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { getTestId } from '@shared/util/getTestId';
import { EditFieldContainer2, PrintContent } from './EditFieldContainer2';
import { EditFieldContainerProps } from './EditFieldContainerProps';

export function EditFieldContainer(props: EditFieldContainerProps) {
  const responsiveForms = useResponsiveFormContext();
  if (responsiveForms) {
    return <EditFieldContainer2 {...props} />;
  } else {
    return <EditFieldContainer1 {...props} />;
  }
}

function EditFieldContainer1({
  component,
  children,
  icon = getComponentIcon(component),
  label = getComponentLabel(component),
  placeholder = getComponentPlaceholder(component),
  showIcon = true,
  showLabel = false,
  indentLabel = false,
  showPlaceholder = false,
  showClearButton = false,
  error,
  onPress,
  onClear,
  reserveIconSpace = false,
  includeWrapperPadding = true,
  compactTop = false,
  compactBottom = false,
  testID = getTestId('card-field-cards', component.id),
  anchorName,
  printContent,
}: EditFieldContainerProps) {
  const colors = useContext(ColorThemeContext);
  const isDisplayable = isDisplayableComponent(component);
  const neverHasIcon = component.id === 'name' && isNil(icon);
  const shouldHaveIcon = (showIcon || reserveIconSpace) && isDisplayable && !neverHasIcon;
  const renderIcon = showIcon && shouldHaveIcon && !isNil(icon);
  const renderLabel = showLabel && isDisplayable && !isNil(label);
  const renderPlaceholder = showPlaceholder && isDisplayable && !isNil(placeholder);
  const renderClearButton = showClearButton && !isNil(onClear);
  const hasError = !isNil(error);

  return (
    <>
      <PrintContent label={label} content={printContent} />
      <div className="no-print no-print-content">
        <MBTouchableOpacity onPress={onPress} testID={testID}>
          <MBView
            style={[
              styles.wrapper,
              includeWrapperPadding && styles.wrapperPadding,
              hasError && { backgroundColor: colors.error.background },
            ]}
          >
            <View style={{ anchorName }}>
              <MBView
                style={[
                  styles.row,
                  includeWrapperPadding && styles.rowWithWrapperPadding,
                  compactTop && styles.compactRow,
                  compactBottom && styles.compactRowBottom,
                ]}
              >
                {renderIcon && (
                  <RNView style={[styles.icon]}>
                    <MBIconV2
                      name={icon}
                      size={40}
                      color={hasError ? colors.error.foreground : colors.accent}
                    />
                  </RNView>
                )}
                {shouldHaveIcon && !renderIcon && !reserveIconSpace && (
                  <RNView style={styles.icon}>
                    <MBIconV2
                      name="squares-stacked"
                      size={40}
                      color={hasError ? colors.error.foreground : colors.accent}
                    />
                  </RNView>
                )}
                {reserveIconSpace && !renderIcon && (
                  <RNView style={[styles.icon, styles.iconSpace]} />
                )}
                <RNView
                  style={[
                    styles.content,
                    compactTop && styles.compactContentTop,
                    compactBottom && styles.compactContentBottom,
                  ]}
                >
                  {renderLabel && (
                    <Text
                      size="overline"
                      weight="semibold"
                      danger={hasError}
                      inactive={!hasError}
                      style={{
                        ...cssStyles.label,
                        ...(indentLabel ? cssStyles.indentedLabel : undefined),
                      }}
                    >
                      {label}
                    </Text>
                  )}
                  {renderPlaceholder && (
                    <MBView style={styles.placeholder}>
                      <Text size="body1" inactive>
                        {placeholder}
                      </Text>
                    </MBView>
                  )}
                  {children}
                </RNView>
                {renderClearButton && (
                  <MBView style={styles.clearButton}>
                    <MBClearButton
                      onPress={onClear}
                      color={hasError ? colors.foregroundInactive : undefined}
                    />
                  </MBView>
                )}
              </MBView>
            </View>
          </MBView>
          {hasError && (
            <MBView style={[styles.error]}>
              <Text size="body2" danger>
                {error}
              </Text>
            </MBView>
          )}
        </MBTouchableOpacity>
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'column',
    alignItems: 'stretch',
    borderRadius: grid(1),
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
  },
  wrapperPadding: {
    marginVertical: grid(0.125),
    marginHorizontal: grid(0.75),
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 60,
    backgroundColor: 'transparent',
    justifyContent: 'space-between',
    paddingHorizontal: grid(3),
  },
  rowWithWrapperPadding: {
    paddingHorizontal: grid(2),
  },
  icon: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexDirection: 'row',
    height: '100%',
    marginRight: grid(2),
    paddingTop: grid(1.5),
  },
  iconSpace: {
    width: grid(5),
  },
  placeholder: {
    paddingVertical: grid(1.25),
  },
  clearButton: {
    justifyContent: 'center',
    flexDirection: 'row',
    height: '100%',
    alignItems: 'flex-start',
    marginLeft: grid(2),
    paddingVertical: grid(1.25),
  },
  content: {
    flexGrow: 1,
    flexShrink: 1,
    paddingVertical: grid(1.5),
  },
  compactRow: {
    minHeight: grid(4),
    paddingTop: 0,
  },
  compactRowBottom: {
    minHeight: grid(4),
    paddingBottom: 0,
  },
  compactContentTop: {
    paddingTop: 0,
  },
  compactContentBottom: {
    paddingBottom: 0,
  },
  error: {
    paddingLeft: grid(3),
    paddingTop: grid(1),
    paddingBottom: grid(2),
    alignSelf: 'flex-start',
  },
});

const cssStyles: Record<string, CSSProperties> = {
  label: {
    marginBottom: grid(0.5),
  },
  indentedLabel: {
    marginLeft: grid(7),
  },
};
